/**
 * Presents one collapsible setup area shared by connector dialogs.
 *
 * Providers own their fields, while Vault owns the visual hierarchy used to
 * compare connection, source, and schedule choices without losing form state.
 */
import type { ReactNode } from 'react'
import { ChevronDown } from 'lucide-react'

import { cn } from '@/lib/utils'

export function ConnectorDialogSection({
  title,
  summary,
  open,
  onOpenChange,
  children,
}: {
  title: string
  summary?: ReactNode
  open: boolean
  onOpenChange: (open: boolean) => void
  children: ReactNode
}) {
  return (
    <section className="border-b border-slate-100 last:border-b-0">
      <button
        type="button"
        className="flex w-full items-start justify-between gap-3 px-6 py-[15px] text-left transition-colors hover:bg-slate-50/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-300"
        aria-expanded={open}
        onClick={() => onOpenChange(!open)}
      >
        <span className="min-w-0">
          <span className="block text-[13.5px] font-semibold leading-[16.5px] text-slate-900">
            {title}
          </span>
          {!open && summary && (
            <span className="mt-0.5 block truncate text-xs leading-4 text-slate-500">
              {summary}
            </span>
          )}
        </span>
        <ChevronDown
          className={cn(
            'mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform duration-150',
            open && 'rotate-180',
          )}
        />
      </button>
      {open && <div className="px-6 pb-[18px] pt-0.5">{children}</div>}
    </section>
  )
}
